import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useIntl} from 'react-intl';
import {useLocation, useNavigate} from 'react-router-dom';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import {AppIconButton} from '@components/Core/buttons/AppIconButton/AppIconButton';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import {HiOutlineUserCircle} from '@react-icons/all-files/hi/HiOutlineUserCircle';
import {GiFat} from '@react-icons/all-files/gi/GiFat';
import {HiOutlineChevronDown} from '@react-icons/all-files/hi/HiOutlineChevronDown';
import {HiOutlineChevronUp} from '@react-icons/all-files/hi/HiOutlineChevronUp';
import isEmpty from 'lodash.isempty';
import get from 'lodash.get';
import AdminSubMenu from './SubMenus/AdminSubMenu';
import AccountSubMenu from './SubMenus/AccountSubMenu';
import {startIntroduction} from '../../../AccountSettings/accountSettingsSlice';
import ConfirmationModal from '../../../shared/ConfirmationModal/ConfirmationModal';
import {
    selectUser, selectFeatures, selectPermissions, selectHasUserPermissionOnRoot, selectGetIsDelegation,
    selectDelegationUser, selectAccount, selectUserAttributes, selectAccessToken
} from '../../../Login/userSlice';
import permissionsService from '../../../../utils/permissionsService';
import {selectBrand} from '../../../AdminPanel/ThemesAndStyles/brandSettingsSlice';
import {getAvailableAdminRoutes} from '../../../../utils/routes';
import {
    ACCOUNT_PATH, ADMIN_PATH, DASHBOARD_PATH, OAUTH_PATH
} from '../../../../../constants';
import {getAvatarSrc} from '../../../../utils/helpers';
import {useGet2FAEnforcementStatus} from '@hooks/useGet2FAEnforcementStatus';
import './userMenu.less';

export default function UserMenu() {
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const user = useSelector(selectUser);
    const features = useSelector(selectFeatures);
    const permissions = useSelector(selectPermissions);
    const hasUserPermissionOnRoot = useSelector(selectHasUserPermissionOnRoot);
    const isDelegation = useSelector(selectGetIsDelegation);
    const delegationUser = useSelector(selectDelegationUser);
    const account = useSelector(selectAccount);
    const displayUser = delegationUser || user;
    const enabledRoutes = getAvailableAdminRoutes({
        user, features, permissions, hasUserPermissionOnRoot, isDelegation
    });
    const isFatIcons = get(user, 'data.fatIcons');
    const avatarSrc = getAvatarSrc(get(displayUser, 'data.avatar') || displayUser?.avatar);
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState(null);
    const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
    const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const isMenuOpen = !!anchorEl;
    const menuId = 'user-menu';
    const accountIcon = !isFatIcons ? <HiOutlineUserCircle aria-hidden /> : <GiFat aria-hidden />;
    const brand = useSelector(selectBrand);
    const {show_intro: showIntro} = useSelector(selectUserAttributes);
    const {isBlocking: is2FASetupEnforced} = useGet2FAEnforcementStatus();
    const accessToken = useSelector(selectAccessToken);
    const isScopedUser = permissionsService.getIsScopedUser({accessToken});

    function handleMenuOpen(event) {
        setAnchorEl(event.currentTarget);
    }

    function handleMenuClose() {
        setAnchorEl(null);
        setIsAdminMenuOpen(false);
        setIsAccountMenuOpen(false);
    }

    function onLogOut() {
        const logoutUrl = brand?.url_logout
            ? `${OAUTH_PATH}/logout?redirect=${encodeURIComponent(brand.url_logout)}`
            : `${OAUTH_PATH}/logout`;
        window.location.href = logoutUrl;
    }

    function getIsActiveClass(route) {
        return location.pathname.startsWith(route) ? 'is-active' : '';
    }

    function toggleAdminMenu() {
        setIsAdminMenuOpen(!isAdminMenuOpen);
        setIsAccountMenuOpen(false);
    }

    function toggleAccountMenu() {
        setIsAccountMenuOpen(!isAccountMenuOpen);
        setIsAdminMenuOpen(false);
    }

    function userInfoItem() {
        const firstName = displayUser?.firstName || displayUser?.first_name || '';
        const lastName = displayUser?.lastName || displayUser?.last_name || '';
        const email = displayUser?.email || '';
        const type = displayUser?.type;
        return (
            <MenuItem
                divider
                disabled
                classes={{root: 'menu-item user-info-item'}}
            >
                <div className="user-menu-info-item">
                    <div className="icon-container">
                        <Avatar
                            classes={{root: 'user-menu-item-avatar'}}
                            alt={`${firstName} ${lastName}`}
                            src={avatarSrc}
                        >
                            {firstName.substr(0, 1)}
                        </Avatar>
                    </div>
                    <div className="info-container">
                        <div className="full-name">
                            {`${firstName} ${lastName}`}
                        </div>
                        <div className="email">
                            <small>
                                {email}
                            </small>
                        </div>
                        {type && (
                            <div className="type">
                                {intl.formatMessage({id: `esl.account.${type.toLowerCase()}`, defaultMessage: ''})}
                            </div>
                        )}
                    </div>
                </div>
            </MenuItem>
        );
    }

    function goToPage(link) {
        navigate(link);
        handleMenuClose();
    }

    async function onWalkthrough() {
        const wasSuccessful = await dispatch(startIntroduction());
        if (wasSuccessful) {
            if (showIntro && location.pathname === DASHBOARD_PATH) {
                window.location.reload(false);
            }
            goToPage(DASHBOARD_PATH);
        }
    }

    function renderMenu() {
        return (
            <Menu
                anchorEl={anchorEl}
                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                id={menuId}
                keepMounted
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                open={isMenuOpen}
                onClose={handleMenuClose}
                variant="menu"
                MenuListProps={{
                    disablePadding: true,
                    classes: {root: 'user-menu-list'}
                }}
            >
                {userInfoItem()}
                {!isScopedUser && !isDelegation && (
                    <MenuItem
                        classes={{root: `menu-item sub-menu-header link ${getIsActiveClass(ACCOUNT_PATH)}`}}
                        selected={location.pathname.startsWith(ACCOUNT_PATH)}
                        disabled={is2FASetupEnforced}
                    >
                        <ListItemText
                            onClick={() => goToPage(ACCOUNT_PATH)}
                            primary={intl.formatMessage({id: 'esl.generic.my_account', defaultMessage: ''})}
                            classes={{root: 'menu-item-title'}}
                            data-wdio="test-header-menu-account-item"
                        />
                        {!is2FASetupEnforced && (
                            <ListItemSecondaryAction>
                                <IconButton
                                    aria-label={intl.formatMessage({id: 'esl.generic.expand', defaultMessage: ''})}
                                    onClick={toggleAccountMenu}
                                    size="small"
                                    data-wdio="test-header-expand-menu-account-items"
                                >
                                    {isAccountMenuOpen
                                        ? <HiOutlineChevronUp className="chevron-icon" />
                                        : <HiOutlineChevronDown className="chevron-icon" />}
                                </IconButton>
                            </ListItemSecondaryAction>
                        )}
                    </MenuItem>
                )}
                {!isScopedUser && !isDelegation && !is2FASetupEnforced && <AccountSubMenu isOpen={isAccountMenuOpen} onClick={handleMenuClose} />}
                {!isScopedUser && !isDelegation && !isEmpty(enabledRoutes) && (
                    <MenuItem
                        classes={{root: `menu-item sub-menu-header link ${getIsActiveClass(ADMIN_PATH)}`}}
                        disabled={is2FASetupEnforced}
                    >
                        <ListItemText
                            onClick={() => goToPage(ADMIN_PATH)}
                            primary={intl.formatMessage({id: 'esl.generic.admin', defaultMessage: ''})}
                            classes={{root: 'menu-item-title'}}
                        />
                        {!is2FASetupEnforced && (
                            <ListItemSecondaryAction>
                                <IconButton
                                    aria-label={intl.formatMessage({id: 'esl.generic.expand', defaultMessage: ''})}
                                    onClick={toggleAdminMenu}
                                    size="small"
                                    data-wdio="test-header-expand-menu-admin-items"

                                >
                                    {isAdminMenuOpen
                                        ? <HiOutlineChevronUp className="chevron-icon" />
                                        : <HiOutlineChevronDown className="chevron-icon" />}
                                </IconButton>
                            </ListItemSecondaryAction>
                        )}
                    </MenuItem>
                )}
                {!isScopedUser && !isDelegation && !isEmpty(enabledRoutes) && !is2FASetupEnforced && <AdminSubMenu isOpen={isAdminMenuOpen} onClick={handleMenuClose} />}
                {!isScopedUser && (
                    <MenuItem
                        classes={{root: 'menu-item link'}}
                        onClick={() => setIsConfirmationModalOpen(true)}
                        disabled={is2FASetupEnforced}
                    >
                        <span className="menu-item-text">
                            {intl.formatMessage({id: 'esl.generic.walkthrough', defaultMessage: ''})}
                        </span>
                    </MenuItem>
                )}
                <MenuItem classes={{root: 'menu-item link'}} onClick={onLogOut}>
                    <span className="link">
                        {intl.formatMessage({id: 'esl.generic.logout', defaultMessage: ''})}
                    </span>
                </MenuItem>
            </Menu>
        );
    }

    if (isEmpty(user)) {
        return null;
    }

    return (
        <div className="user-menu">
            <div className="user-menu-container">
                <AppIconButton
                    aria-label={intl.formatMessage({id: 'esl.account.menu.label', defaultMessage: ''})}
                    aria-controls={menuId}
                    aria-haspopup="true"
                    className={`user-menu-button${avatarSrc ? ' user-menu-button--has-avatar' : ''}`}
                    onClick={handleMenuOpen}
                    data-wdio="test-header-menu-icon-btn"
                >
                    {avatarSrc ? (
                        <Avatar
                            classes={{root: 'user-menu-avatar'}}
                            alt={`${user.firstName} ${user.lastName}`}
                            src={avatarSrc}
                        />
                    ) : accountIcon}
                </AppIconButton>
            </div>
            {renderMenu()}
            <ConfirmationModal
                isOpen={isConfirmationModalOpen}
                descriptionKeys={[showIntro ? 'page.dashboard.intro.restartMessage' : 'page.dashboard.intro.startMessage']}
                setIsOpen={setIsConfirmationModalOpen}
                onConfirm={onWalkthrough}
            />
        </div>
    );
}
